import { CollectionSkeleton } from '@/components/features/Collection/CollectionsGrid/CollectionSkeleton';
import { DebouncedSearchBar } from '@/components/features/Search/DebouncedSearchBar';
import { SearchChainSwitcher } from '@/components/features/layout/Header/SearchChainSwitcher';
import { FormatCryptoCurrency } from '@/components/primitives';
import { CollectionCard } from '@/components/shared/CollectionCard';
import { PageHeader } from '@/components/shared/PageHeader';
import { Sticky } from '@/config/sticky-layout';
import { useMarketplaceChain } from '@/hooks/use-marketplace-chain';
import { getSSRChain } from '@/lib/reservoir/get-ssr-chain';
import { DefaultChain } from '@/utils/chains';
import { getSupportedChainByRoutePrefix } from '@/utils/getChain';
import { routes } from '@/utils/routes';
import {
  Box,
  Container,
  Flex,
  Heading,
  SimpleGrid,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { TabsBarItem, TabsBarWrapper } from '@sphere/ui';
import { type GetServerSideProps, type InferGetServerSidePropsType, type NextPage } from 'next';
import { NextSeo } from 'next-seo';
import useTranslation from 'next-translate/useTranslation';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { rem } from 'polished';
import { useEffect, useState } from 'react';
import {
  SearchCollectionItem,
  SearchGameItem,
  SearchResultItem,
  searchGame,
  searchSingleChain,
} from '../api/globalSearch';

enum SearchTab {
  Collections = 'collections',
  Games = 'games',
}

const DEFAULT_TAB = SearchTab.Collections;

const SEARCH_MIN_CHARACTERS = 2;

const Page: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = ({
  initialResults,
}) => {
  const { t } = useTranslation('search');
  const marketplaceChain = useMarketplaceChain();

  const router = useRouter();

  const searchParams = useSearchParams();
  const searchQuery = searchParams.get('q');
  const chain = getSupportedChainByRoutePrefix(searchParams.get('chain') || '') ?? DefaultChain;
  const tab = searchParams.get('tab');
  const activeTab = Object.values(SearchTab).includes(tab as SearchTab) ? tab : DEFAULT_TAB;

  const [isSearching, setSearching] = useState(false);

  const [results, setResults] = useState<SearchResultItem[]>(initialResults);

  const collections = results.filter(
    (result): result is SearchCollectionItem => result.type === 'collection',
  );
  const games = results.filter((result): result is SearchGameItem => result.type === 'game');

  useEffect(() => {
    const search = async () => {
      if ((searchQuery?.length || 0) < SEARCH_MIN_CHARACTERS) {
        setResults([]);
        return;
      }

      setSearching(true);

      const res = await fetch(
        `/api/globalSearch?query=${searchQuery}&searchChain=${chain.routePrefix}&limit=20`,
      ).then(res => res.json());

      setResults(res.results);
      setSearching(false);
    };

    search();
  }, [searchQuery, chain]);

  const handleChangeSearchQuery = (query: string) => {
    if (!router.isReady) return;

    const { q: prevQuery, ...queryParams } = router.query;

    if (query === prevQuery) return;

    router.replace({ query: { ...queryParams, ...(!!query && { q: query }) } }, undefined, {
      shallow: true,
    });
  };

  const handleChangeChain = (chain: string) => {
    if (!router.isReady) return;

    const { chain: prevChain, ...queryParams } = router.query;

    if (chain === prevChain) return;

    router.replace({ query: { ...queryParams, ...(!!chain && { chain }) } }, undefined, {
      shallow: true,
    });
  };

  return (
    <>
      <NextSeo
        title={t('tabs.collections.page-title')}
        description={t('tabs.collections.page-description')}
      />
      <PageHeader
        containerVariant="breakout"
        pages={[
          {
            name: t('common:crumbs.home'),
            href: routes.home(marketplaceChain.routePrefix),
          },
          {
            name: t('common:crumbs.search'),
            href: routes.search,
          },
        ]}
      />

      <Container mb="space.80" variant="breakout">
        <Heading
          as="h1"
          maxWidth={rem(1000)}
          fontSize={{ base: '2xl', md: '2xl', lg: '5xl' }}
          data-testid="search-header-empty-title"
        >
          {t('results-bar.results.header')}
        </Heading>
        <Sticky
          display="flex"
          flexDir="column"
          gap="space.24"
          bg="blackBg"
          element="action-bar"
          zIndex="sticky-layout"
          pt="space.16"
          pb="space.24"
        >
          <SearchChainSwitcher searchChain={chain.routePrefix} onChangeChain={handleChangeChain} />
          <DebouncedSearchBar
            initialSearchQuery={searchQuery || ''}
            onSearchQueryChange={handleChangeSearchQuery}
          />
        </Sticky>

        <Tabs isLazy index={activeTab === SearchTab.Games ? 1 : 0}>
          <TabsBarWrapper stackProps={{ as: TabList }} mb="space.16">
            <TabsBarItem
              id={SearchTab.Collections}
              isActive={activeTab === SearchTab.Collections}
              onClick={() =>
                router.replace(
                  { query: { ...router.query, tab: SearchTab.Collections } },
                  undefined,
                  {
                    shallow: true,
                  },
                )
              }
              count={collections.length}
            >
              {t('tabs.collections.label')}
            </TabsBarItem>
            <TabsBarItem
              id={SearchTab.Games}
              isActive={activeTab === SearchTab.Games}
              onClick={() =>
                router.replace({ query: { ...router.query, tab: SearchTab.Games } }, undefined, {
                  shallow: true,
                })
              }
              count={games.length}
            >
              {t('tabs.games.label')}
            </TabsBarItem>
          </TabsBarWrapper>
          {!!results.length && !isSearching ? (
            <TabPanels>
              <TabPanel px={0}>
                {collections.length ? (
                  <SimpleGrid columns={{ base: 1, md: 2, lg: 3, '2xl': 4 }} spacing="space.24">
                    {collections.map(({ data: collection }) => (
                      <CollectionCard
                        key={collection.id}
                        mb="space.12"
                        image={{
                          src: collection.banner || collection.image,
                          alt: collection.name,
                        }}
                        link={{
                          href: routes.collections.detail(
                            collection.contract || '',
                            collection.chainRoutePrefix,
                          ),
                        }}
                        creator={{
                          avatar: collection.image,
                        }}
                        title={collection.name}
                        description={collection.description}
                      >
                        <Flex gap="space.18" alignItems="flex-end" flexGrow={1}>
                          {collection.tokenCount && (
                            <Flex direction="column" gap="space.2">
                              <Text color="gray.6" fontSize="xs">
                                {t('results.collections.labels.tokens')}
                              </Text>
                              <Text color="gray.1" fontSize="sm" fontWeight="bold">
                                {collection.tokenCount}
                              </Text>
                            </Flex>
                          )}
                          {!!collection.floorAskPrice && (
                            <Flex direction="column" gap="space.2">
                              <Text color="gray.6" fontSize="xs">
                                {t('results.collections.labels.floor')}
                              </Text>
                              <FormatCryptoCurrency
                                amount={collection.floorAskPrice.amount?.decimal}
                                symbol={collection.floorAskPrice.currency?.symbol}
                                maximumFractionDigits={2}
                                logoHeight={14}
                                fontWeight="medium"
                                fontSize="sm"
                                color="gray.1"
                              />
                            </Flex>
                          )}
                          {!!collection.allTimeVolume && (
                            <Flex direction="column" gap="space.2">
                              <Text color="gray.6" fontSize="xs">
                                {t('results.collections.labels.volume')}
                              </Text>
                              <FormatCryptoCurrency
                                amount={collection.allTimeVolume}
                                maximumFractionDigits={2}
                                logoHeight={14}
                                fontWeight="medium"
                                fontSize="sm"
                                color="gray.1"
                              />
                            </Flex>
                          )}
                        </Flex>
                      </CollectionCard>
                    ))}
                  </SimpleGrid>
                ) : (
                  <Text color="gray.6">
                    {t('no-results-type', {
                      type: 'collections',
                      chain: chain?.name ?? '',
                    })}
                  </Text>
                )}
              </TabPanel>
              <TabPanel px={0}>
                {games.length ? (
                  <SimpleGrid columns={{ base: 1, md: 2, lg: 3, '2xl': 4 }} spacing="space.24">
                    {games.map(({ data: game }) => (
                      <CollectionCard
                        key={game.id}
                        mb="space.12"
                        image={{
                          src: game.logo?.src,
                          alt: game.name ?? '',
                        }}
                        link={{
                          href: routes.games.detail(game.slug, game.chainRoutePrefix),
                        }}
                        creator={{
                          avatar: game.avatar?.src,
                        }}
                        title={game.name}
                        description={game.description}
                      >
                        <Flex gap="space.18" alignItems="flex-end" flexGrow={1}>
                          <Flex direction="column" gap="space.2">
                            <Text color="gray.6" fontSize="xs">
                              {t('results.games.labels.collections')}
                            </Text>
                            <Text color="gray.1" fontSize="sm" fontWeight="bold">
                              {game.contracts.length}
                            </Text>
                          </Flex>
                        </Flex>
                      </CollectionCard>
                    ))}
                  </SimpleGrid>
                ) : (
                  <Text color="gray.6">
                    {t('no-results-type', { type: 'games', chain: chain?.name ?? '' })}
                  </Text>
                )}
              </TabPanel>
            </TabPanels>
          ) : (
            <Box mt="space.32" color="gray.6">
              {isSearching ? (
                <SimpleGrid columns={{ base: 1, md: 3, '2xl': 4 }} spacing="space.24">
                  {[...Array(12)].map((_, index) => (
                    <CollectionSkeleton key={index} />
                  ))}
                </SimpleGrid>
              ) : searchQuery && searchQuery.length >= SEARCH_MIN_CHARACTERS ? (
                <Text>{t('no-results', { chain: chain?.name ?? '' })}</Text>
              ) : (
                <Text>{t('no-query', { chain: chain?.name ?? '' })}</Text>
              )}
            </Box>
          )}
        </Tabs>
      </Container>
    </>
  );
};

type Params = {
  q: string;
  chain?: string;
};

type Props = {
  initialResults: SearchResultItem[];
};

export const getServerSideProps: GetServerSideProps<Props, Params> = async ctx => {
  const chain = getSSRChain(ctx);
  const query = (ctx.query.q ?? '') as string;
  const limit = 20;

  if (query) {
    const collections = await searchSingleChain(chain, query, limit);
    const games = await searchGame(chain, query, limit);

    return {
      props: {
        initialResults: [...collections, ...games],
      },
    };
  }

  return {
    props: {
      initialResults: [],
    },
  };
};

export default Page;
